import { useEffect, useRef, type RefObject } from 'react';
import { focusFirstFocusable, getFocusableElements } from './focusTrap.js';

/**
 * True when `el` lives in a different overlay panel than `panel`
 * (e.g. Modal portaled to body while a Drawer trap still runs).
 */
function focusInForeignOverlay(
  panel: HTMLElement,
  active: Element | null,
): boolean {
  if (!active || !(active instanceof Element)) return false;
  if (panel.contains(active)) return false;
  const host = active.closest('[aria-modal="true"], [role="dialog"], [data-wp-overlay-panel]');
  return Boolean(host && host !== panel && !panel.contains(host));
}

/** Focus trap + restore for modal overlays (Modal, Drawer). */
export function useOverlayFocusTrap(opened: boolean, panelRef: RefObject<HTMLElement | null>) {
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!opened) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (panel) focusFirstFocusable(panel);
    });
    return () => {
      cancelAnimationFrame(raf);
      returnFocusRef.current?.focus?.();
      returnFocusRef.current = null;
    };
  }, [opened, panelRef]);

  useEffect(() => {
    if (!opened) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      // Nested portaled overlay (Modal over Drawer): leave Tab to the child trap.
      if (focusInForeignOverlay(panel, document.activeElement)) return;
      // Portaled menu (combobox, phone country). Its own handler moves focus.
      if (document.activeElement?.closest('[data-wp-popover-panel]')) return;
      const focusables = getFocusableElements(panel);
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      // Reclaim focus if it escaped the panel (and not into a nested overlay).
      if (!panel.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (focusables.length < 2) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [opened, panelRef]);
}
